/**
 * AI Batting Diagnostics — lookup tables (mirror of
 * `wcc4-backend/src/modules/ai-analytics/wicket-constants.ts`).
 *
 * Populated from the live Unity Editor via reflection on 2026-06-04.
 * Companion docs:
 *   - wicket-constants.client-corrections.md (rationale / validation)
 *   - wicket-constants.client-values.md       (full id→name tables)
 *
 * Of the 6 fields: only wicket_type is on the wire today (codes 1–8
 * in wku/wka). The other 5 tables are correct client mirrors; Unity
 * needs new emitters before those IDs appear on the analytics wire.
 *
 * Keep both constants files in sync — small + stable, manual copy.
 */

// ═════════════════════════════════════════════════════════════════
// 1. wicket_type — ✅ EMITTED (codes 1–8 in wku/wka)
// ═════════════════════════════════════════════════════════════════

export const WICKET_TYPES = ['bowled', 'caught', 'lbw', 'run_out', 'stumped'] as const;
export type WicketType = (typeof WICKET_TYPES)[number];

export const WICKET_TYPE_LABELS: Record<WicketType, string> = {
  bowled: 'Bowled',
  caught: 'Caught',
  lbw: 'LBW',
  run_out: 'Run Out',
  stumped: 'Stumped',
};

/**
 * Production analytics codes 1–8 in wku/wka. Source: GameModel.WicketBall.
 * Codes 3, 5, 7, 8 collapse to 'caught' in the spec taxonomy.
 * 🔴 Do NOT decode via `DecisonType` (UI enum, collides destructively).
 */
export const WICKET_CODES: Record<number, string> = {
  1: 'Bowled',
  2: 'LBW',
  3: 'Caught',
  4: 'Run out',
  5: 'Caught behind',
  6: 'Stumped',
  7: 'Caught and bowled',
  8: 'Caught (slip)',
};

export const WICKET_CODE_TO_WICKET_TYPE: Record<number, WicketType> = {
  1: 'bowled',
  2: 'lbw',
  3: 'caught',
  4: 'run_out',
  5: 'caught',
  6: 'stumped',
  7: 'caught',
  8: 'caught',
};

export const WK_ANALYTICS_DELIMITER = '|';
export const WK_ANALYTICS_MAX_WICKETS = 10;
export const WK_ANALYTICS_MAX_CHARS = 255;

// ═════════════════════════════════════════════════════════════════
// 2. shot_type — Unity `ShotElevation` (3 members)
// ═════════════════════════════════════════════════════════════════

/**
 * `Both = 3` is the bitwise OR (Grounded | Lofted) — a shot's
 * capability. Executed shots resolve to 1 or 2, but capability-mode
 * emitters may legitimately send 3.
 * ⚠️ Not on the wire today.
 */
export const SHOT_TYPES: Record<number, string> = {
  1: 'Grounded',
  2: 'Lofted',
  3: 'Both',
};

// ═════════════════════════════════════════════════════════════════
// 3. shot_id — Unity `shots` enum (406 members)
// ═════════════════════════════════════════════════════════════════

/**
 * The full `shots` enum from GroundControllerBase.cs:11695 (the
 * production base class). 406 members, reflection-verified.
 * `0 = Empty` is the "no shot" sentinel.
 *
 * ⚠️ Not on the wire today (production source if added: `shotPlayed`).
 * Names are the enum identifiers — the dashboard prettifies them
 * (`CoverDrive2` → "Cover Drive") in the display layer.
 *
 * 🔴 NOT `Segment` 1..10 (that's Firebase zone_id, see
 * WICKET_LOCATION_ZONES below). NOT the 158-member `shots` enum in
 * the dead BowlingControlsPracticeMode. The brief's 47/48/49 were
 * placeholders that don't match (47 = LateCut, 48 = LegGlance).
 */
export const SHOT_IDS: Record<number, string> = {
  0: 'Empty',
  1: 'ShortPitch_LongOnSlog',
  2: 'CoverSlog',
  3: 'StraightSlog',
  4: 'DownTheTrackStraightSlog',
  5: 'DownTheTrackOffSideLoft',
  6: 'DownTheTrackHittingLegSide',
  7: 'StepOutOffSlog',
  8: 'MidOnSlog',
  9: 'MidWicketSlog',
  10: 'SquareSlog',
  11: 'DownTheTrackDefensiveShot',
  12: 'FrontFootDefense',
  13: 'FrontFootDefense01',
  14: 'FrontFootDefense02',
  15: 'FrontFootDefense03',
  16: 'FrontFootDefense04',
  17: 'FrontFootDefense05',
  18: 'BackFootDefense',
  19: 'BackFootDefense01',
  20: 'BackFootDefense02',
  21: 'BackFootDefense03',
  22: 'BF_Defense_LegSide',
  23: 'BF_Defense_OffSide',
  24: 'BF_Defense_Straight',
  25: 'FF_Defense_LegSide01',
  26: 'FF_Defense_LegSide02',
  27: 'FF_Defense_OffSide01',
  28: 'FF_Defense_OffSide02',
  29: 'FF_Defense_Straight01',
  30: 'FF_Defense_Straight02',
  31: 'DTT_Defense_LegSide01',
  32: 'DTT_Defense_LegSide02',
  33: 'DTT_Defense_OffSide01',
  34: 'DTT_Defense_OffSide02',
  35: 'DTT_Defense_Straight01',
  36: 'DTT_Defense_Straight02',
  37: 'FF_Defense_LegSide01_LH',
  38: 'FF_Defense_LegSide02_LH',
  39: 'FF_Defense_OffSide01_LH',
  40: 'LateCut3',
  41: 'BF_ThirdMan01',
  42: 'BF_ThirdMan02',
  43: 'BF_ThirdMan03',
  44: 'BF_Thirdman04',
  45: 'BF_SquareThirdMan',
  46: 'LegGlance_Frontfoot',
  47: 'LateCut',
  48: 'LegGlance',
  49: 'BF_DeepThirdMan',
  50: 'FF_DeepFineLeg',
  51: 'FF_DeepFineLeg01',
  52: 'FF_DeepFineLeg02',
  53: 'FF_FineLeg',
  54: 'FF_ShortFineLeg',
  55: 'ThirdManGlance',
  56: 'SquareCut11',
  57: 'SquareCut12',
  58: 'SquareCut',
  59: 'BF_BackwardPoint01',
  60: 'BF_BackwardPoint02',
  61: 'BF_DeepBackwardPoint01',
  62: 'BF_DeepBackwardPoint02',
  63: 'PointSlogHip',
  64: 'Spin_BackwardPoint',
  65: 'LoftedSquareCut2',
  66: 'SquareCut_New',
  67: 'WideSquareCut',
  68: 'BF_DeepFwdMidWicket01',
  69: 'BF_DeepFwdMidWicket02',
  70: 'BF_DeepFwdMidWicket03',
  71: 'BF_DeepFwdMidWicket04',
  72: 'BF_DeepMidWicket01',
  73: 'BF_DeepMidWicket02',
  74: 'BF_DeepMidWicket03',
  75: 'LoftedPullShot',
  76: 'GeorgeBailey_DeepMidWicketSlog',
  77: 'HookShot',
  78: 'DeepMidWicketShot',
  79: 'PullShotChest01',
  80: 'PullShotChest',
  81: 'TravisHead_SlapShot',
  82: 'BackFootDrive',
  83: 'BackFootPush',
  84: 'CoverDrive2',
  85: 'frontFootOffDrive',
  86: 'OffDrive3',
  87: 'BF_Cover',
  88: 'BF_CoverPoint',
  89: 'BF_DeepCover',
  90: 'BF_DeepCoverPoint',
  91: 'BF_DeepExtraCover',
  92: 'BF_ExtraCover',
  93: 'FF_CoverPoint',
  94: 'FF_DeepCoverPoint',
  95: 'FF_ExtraCover',
  96: 'FF_ShortCover',
  97: 'BF_ShortCover',
  98: 'BF_StraightDrive',
  99: 'StraightDrive',
  100: 'FF_StraightDrive01',
  101: 'FF_StraightDrive02',
  102: 'FF_StraightDrive03',
  103: 'DownTheTrackSpin_StraightDrive',
  104: 'StraightDrive_Pace',
  105: 'BF_DeepMidOn01',
  106: 'BF_DeepMidOn02',
  107: 'BF_DeepPoint01',
  108: 'BF_DeepPoint02',
  109: 'BF_ForwardSquareLeg',
  110: 'BF_LongOn',
  111: 'BF_MidOn',
  112: 'BF_MidWicket',
  113: 'BF_Point',
  114: 'BF_WideLongOff',
  115: 'BF_WideLongOn01',
  116: 'BF_WideLongOn02',
  117: 'FF_DeepMidOff01',
  118: 'FF_DeepMidOff02',
  119: 'FF_DeepMidOn01',
  120: 'FF_DeepMidOn02',
  121: 'FF_DeepMidOn03',
  122: 'FF_DeepMidWicket',
  123: 'FF_LongLeg',
  124: 'FF_LongOn',
  125: 'FF_MidOff',
  126: 'FF_MidOn',
  127: 'FF_MidWicket',
  128: 'FF_ShortMidOff',
  129: 'FF_SquareFineLeg01',
  130: 'FF_SquareFineLeg02',
  131: 'FF_WideLongOn01',
  132: 'FF_WideLongOn02',
  133: 'Well_Left_Pace01',
  134: 'Well_Left_Pace02',
  135: 'Well_Left_Pace03',
  136: 'Well_Left_Pace04',
  137: 'Well_Left_Pace05',
  138: 'Well_Left_Pace06',
  139: 'Well_Left_Spin01',
  140: 'Well_Left_Spin02',
  141: 'Well_Left_Spin03',
  142: 'Well_Left_Spin04',
  143: 'WellLeft',
  144: 'WellLeftNormalHeight',
  145: 'LoftedOffDrive',
  146: 'OnSideSlog',
  147: 'DownTheTrackMidOn',
  148: 'DownTheTrackMidOff',
  149: 'Spin_MidwicketPush',
  150: 'Spin_DeepExtraCover',
  151: 'LoftedOnDrive',
  152: 'LoftedCoverDrive',
  153: 'LoftedStraightDrive',
  154: 'OnDrive',
  155: 'SquarePush',
  156: 'LoftedSweepShot',
  157: 'loftStraight',
  158: 'bt6LegGlance',
  159: 'loftLegSide',
  160: 'loftOffSide',
  161: 'powerfulSweepShot',
  162: 'bt6PullShot',
  163: 'bt6HookShot',
  164: 'bt6SquareCut',
  165: 'BackFootStraightDrive',
  166: 'DilsonScoop',
  167: 'HelicoptorShot',
  168: 'MidWicketPush2',
  169: 'MidWicketPush',
  170: 'SquareCut2',
  171: 'SweepShot',
  172: 'OnDrive2',
  173: 'FrontFootPush',
  174: 'LateCut2',
  175: 'Lofted3rdManGlance',
  176: 'OffDrive',
  177: 'LateCut4',
  178: 'MidOffDrive',
  179: 'LeaveTheBallBouncer',
  180: 'Bowled_Expression02',
  181: 'Bowled_Expression01',
  182: 'Bowled_Expression04',
  183: 'Bowled_Expression03',
  184: 'YorkerBall1',
  185: 'MoisesHenriques_DownTheTrack',
  186: 'MichaelKlinger_LongOffSlog',
  187: 'BackfootPunch',
  188: 'Spin_CutShot',
  189: 'ExtraCoverDrive',
  190: 'PointSlog',
  191: 'UpperCut2',
  192: 'LoftedDeepCoverShot',
  193: 'DavidWarner_Switch_Hit',
  194: 'WideHitLow',
  195: 'LoftedSquareCut',
  196: 'LoftedReverseSweep',
  197: 'GlennMaxwell_SwitchHit2',
  198: 'LoftedReverseSweep2',
  199: 'GlennMaxwell_SwitchHit',
  200: 'Spin_CoverPoint',
  201: 'ReverseSweep2',
  202: 'ReverseSweepSpin',
  203: 'ThirdManFlick',
  204: 'UpperCut',
  205: 'Thirdman_UpperCut',
  206: 'DhoniHeliCopter_New',
  207: 'DownTheTrackSpin_LongOn',
  208: 'UnOrthodoxShot2',
  209: 'UnOrthodoxShot1',
  210: 'LoftedMidWicket',
  211: 'PullShotHip01',
  212: 'ChrisLynn_OnSideHeave',
  213: 'Spin_DeepMidWicket',
  214: 'Spin_LegGlance_02',
  215: 'Spin_LegGlance_01',
  216: 'OnSidePush',
  217: 'SweepShotSpin',
  218: 'GeorgeBailey_PullSlog',
  219: 'AaronFinch_BouncerFlick',
  220: 'AaronFinch_FineLegFlick',
  221: 'DeepSquareLeg_Flick',
  222: 'MidOnStrike',
  223: 'ShaneWatson_PullShot',
  224: 'DArcyShort_LongOnSlog',
  225: 'ShortPitch_LongOnPullShot',
  226: 'PullShotHip',
  227: 'PaddleSweep',
  228: 'SwitchHit_To_Sweep',
  229: 'SweepShot2',
  230: 'ScoopShot',
  231: 'BackPush',
  232: 'YorkerBall2',
  233: 'BouncerNew_02',
  234: 'BouncerNew_01',
  235: 'McCullum_UnorthodoxShot',
  236: 'PullShot',
  237: 'BackfootPush',
  238: 'Unorthodox_ThirdmanFlick',
  239: 'ReverseSweep',
  240: 'HookShot2',
  241: 'FF_Loft_DeepCover',
  242: 'FF_Loft_DeepExtraCover',
  243: 'FF_Loft_DeepMidWicket',
  244: 'FF_Loft_LongOff',
  245: 'FF_Loft_StraightLongOn',
  246: 'FF_Loft_WideLongOff',
  247: 'FF_Loft_WideLongOn',
  248: 'FF_Loft_DeepFwdMidWicket',
  249: 'FF_Loft_DeepMidWicket01',
  250: 'FF_Loft_LongOn01',
  251: 'FF_Loft_SquareFineLeg',
  252: 'FF_Loft_Straight',
  253: 'FF_Loft_WideLongOn01',
  254: 'FF_Loft_WideLongOn02',
  255: 'DTT_Loft_DeepCover',
  256: 'DTT_Loft_DeepCoverPoint',
  257: 'DTT_Loft_LongOff',
  258: 'DTT_Loft_Straight',
  259: 'DTT_Loft_StraightLongOn',
  260: 'DTT_Loft_WideLongOff',
  261: 'DTT_Loft_DeepExtraCover',
  262: 'DTT_Loft_DeepFwdMidWicket',
  263: 'DTT_Loft_DeepMidWicket01',
  264: 'DTT_Loft_DeepMidWicket02',
  265: 'DTT_Loft_FwdSquareLeg',
  266: 'DTT_Loft_LongOff01',
  267: 'DTT_Loft_LongOn',
  268: 'DTT_Loft_Straight01',
  269: 'DTT_Loft_Straight02',
  270: 'DTT_Loft_StraightLongOff',
  271: 'DTT_Loft_WideLongOff01',
  272: 'BF_FwdSquareLeg',
  273: 'BF_Loft_DeepFineLeg01',
  274: 'BF_Loft_DeepFineLeg02',
  275: 'BF_Loft_DeepPoint',
  276: 'BF_Loft_FineLeg',
  277: 'BF_Loft_FwdSquareLeg',
  278: 'BF_Loft_LongLeg',
  279: 'BF_Loft_SquareFineLeg',
  280: 'BF_Loft_SquareLeg',
  281: 'BF_Loft_ThirdMan',
  282: 'BF_BackwardPoint01_LH',
  283: 'BF_DeepCoverPoint_LH',
  284: 'BF_DeepExtraCover_LH',
  285: 'BF_DeepFwdMidWicket01_LH',
  286: 'BF_DeepFwdMidWicket03_LH',
  287: 'BF_DeepFwdMidWicket04_LH',
  288: 'BF_DeepMidOn01_LH',
  289: 'BF_DeepPoint01_LH',
  290: 'BF_Loft_DeepFineLeg01_LH',
  291: 'BF_Loft_DeepFineLeg02_LH',
  292: 'BF_Loft_DeepPoint_LH',
  293: 'BF_Loft_FineLeg_LH',
  294: 'BF_Loft_FwdSquareLeg_LH',
  295: 'BF_Loft_LongLeg_LH',
  296: 'BF_Loft_SquareFineLeg_LH',
  297: 'BF_Loft_SquareLeg_LH',
  298: 'BF_Loft_ThirdMan_LH',
  299: 'BF_WideLongOff_LH',
  300: 'FF_DeepFineLeg01_LH',
  301: 'FF_DeepFineLeg02_LH',
  302: 'FF_DeepMidOff02_LH',
  303: 'FF_DeepMidOn01_LH',
  304: 'FF_DeepMidOn02_LH',
  305: 'FF_DeepMidOn03_LH',
  306: 'FF_DeepMidWicket_LH',
  307: 'FF_FineLeg_LH',
  308: 'FF_Loft_DeepCover_LH',
  309: 'FF_Loft_DeepExtraCover_LH',
  310: 'FF_Loft_DeepFwdMidWicket01_LH',
  311: 'FF_Loft_DeepFwdMidWicket02_LH',
  312: 'FF_Loft_DeepMidWicket02_LH',
  313: 'FF_Loft_LongOff_LH',
  314: 'FF_Loft_SquareFineLeg_LH',
  315: 'FF_Loft_Straight_LH',
  316: 'FF_Loft_WideLongOn01_LH',
  317: 'FF_LongLeg_LH',
  318: 'FF_LongOn_LH',
  319: 'FF_SquareFineLeg01_LH',
  320: 'FF_SquareFineLeg02_LH',
  321: 'FF_StraightDrive02_LH',
  322: 'FF_StraightDrive03_LH',
  323: 'FF_WideLongOn01_LH',
  324: 'FF_WideLongOn02_LH',
  325: 'T20_BatShot_01',
  326: 'T20_BatShot_02',
  327: 'T20_BatShot_03',
  328: 'T20_BatShot_04',
  329: 'T20_BatShot_05',
  330: 'T20_BatShot_06',
  331: 'T20_BatShot_07',
  332: 'T20_BatShot_08',
  333: 'T20_BatShot_09',
  334: 'T20_BatShot_10',
  335: 'T20_BatShot_11',
  336: 'T20_BatShot_12',
  337: 'Bouncer_Shot01',
  338: 'Bouncer_Shot02',
  339: 'Bouncer_Shot03',
  340: 'Bouncer_Shot04',
  341: 'WellLeft_LegSide_01',
  342: 'WellLeft_LegSide_02',
  343: 'WellLeft_LegSide_03',
  344: 'T20_Dhoni_Shot01',
  345: 'T20_HardikPandya_Shot01',
  346: 'T20_HardikPandya_Shot02',
  347: 'T20_IshanKishan_Shot01',
  348: 'T20_IshanKishan_Shot02',
  349: 'T20_Jadeja_Shot01',
  350: 'T20_JosButler_Shot01',
  351: 'T20_Maxwell_Shot01',
  352: 'T20_RashidKhan_Shot01',
  353: 'T20_RishabhPant_Shot01',
  354: 'T20_RishabhPant_Shot02',
  355: 'T20_Rizwan_Shot01',
  356: 'T20_Rizwan_Shot02',
  357: 'T20_SKY_Shot01',
  358: 'T20_SKY_Shot02',
  359: 'T20_SKY_Shot03',
  360: 'T20_SKY_Shot04',
  361: 'T20_SKY_Shot05',
  362: 'T20_SKY_Shot06',
  363: 'T20_SKY_Shot07',
  364: 'T20_SKY_Shot08',
  365: 'T20_SKY_Shot09',
  366: 'T20_SKY_Shot10',
  367: 'T20_SKY_Shot11',
  368: 'T20_SKY_Shot12',
  369: 'T20_SKY_Shot13',
  370: 'T20_SKY_Shot14',
  371: 'T20_SKY_Shot15',
  372: 'T20_SKY_Shot16',
  373: 'T20_ViratKohli_Shot01',
  374: 'Batsman_Injury',
  375: 'Batsman_Injury_GoodLength',
  376: 'BenStokes_01',
  377: 'BenStokes_02',
  378: 'JosButler_01',
  379: 'JosButler_02',
  380: 'KLRahul_01',
  381: 'KLRahul_02',
  382: 'KLRahul_03',
  383: 'Livingstone_01',
  384: 'Livingstone_02',
  385: 'Livingstone_03',
  386: 'AshutoshSharma_Battingshot_DeepPoint',
  387: 'DeKnocks_IPL_BattingShot_2024_ExtraCover',
  388: 'ViratKohliBattingshot_DeepPoint',
  389: 'HardikPandya_BattingShot_DeepExtraCover',
  390: 'Kohli_IPL_BattingShot_2024_LongOff',
  391: 'RishabhPant_BattingShot_DeepCover2024',
  392: 'DeKnocks_IPL_BattingShot_2024_ThirdManSquare',
  393: 'DeKock_Battingshot_DeepBackPoint',
  394: 'HardikPandya_BattingShot_LongStop',
  395: 'WCC3_ShubManGill_LongOff_2024',
  396: 'Kohli_LongOn',
  397: 'HardikPandya_BattingShot_DeepForwardMidWicket',
  398: 'SanjuSamson_BattingShot_MidWicketDeep',
  399: 'SuryakumarYadav_BattingShot_FineLegSquare',
  400: 'ViratKohli_Battingshot_DeepSquare',
  401: 'WCC3_DeKock_BattingShot_01_2024',
  402: 'WCC3_Viratkholi_SquareLegDeepBackward_2024',
  403: 'SKY_BattingShot_2024_LongLeg',
  404: 'Shubmagill04_IPL_BattingShot_2024_LongOn',
  405: 'Shubmangill_01_2024_LongOff',
};

// ═════════════════════════════════════════════════════════════════
// 4. batting_shot_zone — Unity `ZoneType` (3 members)
// ═════════════════════════════════════════════════════════════════

export const BATTING_SHOT_ZONES: Record<number, string> = {
  0: 'Neutral',
  1: 'Hot',
  2: 'Cold',
};

// ═════════════════════════════════════════════════════════════════
// 5. delivery_type_id — Unity `BowlingTypeFlat` (16 members,
//    non-contiguous: 0..7 Pace + 10..17 Spin, 8/9 unused)
// ═════════════════════════════════════════════════════════════════

/**
 * Mirrors Unity enum BowlingTypeFlat (BowlingTypeFlat.cs:13-70).
 * Production physics computes it at runtime; ⚠️ not on the wire today.
 * 🔴 NOT BowlingDeliveryType (different integers, never serialized).
 */
export const DELIVERY_TYPES: Record<number, string> = {
  // Pace family (0..7)
  0: 'OutSwing',
  1: 'InSwing',
  2: 'ReverseOutSwing',
  3: 'ReverseInSwing',
  4: 'LegCutter',
  5: 'OffCutter',
  6: 'FastSeam',
  7: 'MediumPace',
  // Spin family (10..17) — 8/9 unused
  10: 'OffSpin',
  11: 'LegSpin',
  12: 'TopSpin',
  13: 'BackSpin',
  14: 'Googly',
  15: 'Doosra',
  16: 'Slider',
  17: 'Flipper',
};

/**
 * Coarse legacy alternative — `int CONTROLLER.BowlerType` (4-way).
 */
export const BOWLER_TYPE: Record<number, string> = {
  0: 'Fast',
  1: 'Off Spinner',
  2: 'Leg Spinner',
  3: 'Medium Fast',
};

/**
 * Two-way Spin/Pace split (Unity BowlingCategory). For high-level
 * chart grouping; derived client-side from any of the above.
 */
export const BOWLING_CATEGORY: Record<number, string> = {
  0: 'Spin',
  1: 'Pace',
};

export const DELIVERY_TYPE_TO_CATEGORY: Record<number, 0 | 1> = {
  0: 1,
  1: 1,
  2: 1,
  3: 1,
  4: 1,
  5: 1,
  6: 1,
  7: 1, // Pace
  10: 0,
  11: 0,
  12: 0,
  13: 0,
  14: 0,
  15: 0,
  16: 0,
  17: 0, // Spin
};

// ═════════════════════════════════════════════════════════════════
// 6. wicket_location_zone — Unity `Service_SwipeDetector.Segment`
//    (10-segment wagon-wheel direction wedges)
// ═════════════════════════════════════════════════════════════════

/**
 * Mirrors Service_SwipeDetector.Segment 1..10. Used as the closest
 * client analogue to a fielding zone — there's no named
 * fielding-position enum anywhere in the client. Both axes are
 * directional (36°/wedge around the batter).
 *
 * IMPORTANT: this value space is DIFFERENT from `SHOT_IDS` even
 * though names like "Cover Drive" overlap. Here `Cover Drive = 3`
 * is a *direction wedge*; in SHOT_IDS, shot identities use
 * different ordinals (e.g. `CoverDrive2 = 84`).
 *
 * ⚠️ Not on the AI analytics wire today (only Firebase events).
 */
export const WICKET_LOCATION_ZONES: Record<number, string> = {
  1: 'Straight Drive',
  2: 'Off Drive',
  3: 'Cover Drive',
  4: 'Square Cut',
  5: 'Late Cut',
  6: 'Defense',
  7: 'Fine Leg',
  8: 'Pull Shot',
  9: 'Mid Wicket',
  10: 'On Drive',
};

// ═════════════════════════════════════════════════════════════════
// 7. shot_timing_zone + shot_timing_direction — batsman timing meter
//    (brief §13, contract realigned 2026-06-05 — see
//    `shot-timing-contract-validation.md`)
// ═════════════════════════════════════════════════════════════════

/**
 * Mirror of backend `SHOT_TIMING_ZONE`. Unity `TimingZone` (QUALITY
 * axis): 0=Perfect (middled), 1=Good, 2=OK (edged), 3=Miss (swung,
 * no contact). Independent from direction — a real shot is both.
 *
 * Paired with the continuous `shot_timing_value` SIGNED `[-1..+1]`
 * float on each WicketEvent (the sign carries the early/late info).
 *
 * 🔴 Earlier draft used a flat 6-state {Perfect/Good/Early/Late/
 * Poor/Missed} — that's the wrong contract. Use `SHOT_TIMING_ZONE` for
 * quality, `SHOT_TIMING_DIRECTION` for direction.
 *
 * ⚠️ Not on the wire today — needs the Unity BattingController to
 * surface `TimingZone` at the shot-trigger frame.
 */
export const SHOT_TIMING_ZONE: Record<number, string> = {
  0: 'Perfect',
  1: 'Good',
  2: 'OK',
  3: 'Miss',
};

/**
 * Mirror of backend `SHOT_TIMING_DIRECTION`. Unity `TimingDirection`
 * (DIRECTION axis): -1=Early, 0=Center, 1=Late. Orthogonal to zone.
 *
 * ⚠️ Not on the wire today — same emitter prerequisite as zone.
 */
export const SHOT_TIMING_DIRECTION: Record<number, string> = {
  [-1]: 'Early',
  0: 'Center',
  1: 'Late',
};

/**
 * Spec fields without a Unity emitter today.
 */
export const PENDING_UNITY_EMITTER = [
  'shot_type',
  'shot_id',
  'batting_shot_zone',
  'delivery_type_id',
  'wicket_location_zone',
  'shot_timing_value',
  'shot_timing_zone',
  'shot_timing_direction',
] as const;

// ═════════════════════════════════════════════════════════════════
// Display helpers (default to numeric ID if unknown).
// ═════════════════════════════════════════════════════════════════

export const shotName = (id: number | null | undefined): string =>
  id == null ? '-' : SHOT_IDS[id] || `Shot ${id}`;

export const deliveryName = (id: number | null | undefined): string =>
  id == null ? '-' : DELIVERY_TYPES[id] || `Type ${id}`;

export const zoneName = (id: number | null | undefined): string =>
  id == null ? '-' : WICKET_LOCATION_ZONES[id] || `Zone ${id}`;

export const wicketTypeLabel = (t: string): string =>
  (WICKET_TYPE_LABELS as Record<string, string>)[t] || t;

export const wicketCodeLabel = (code: number | null | undefined): string =>
  code == null ? '-' : WICKET_CODES[code] || `Code ${code}`;

export const bowlerTypeName = (id: number | null | undefined): string =>
  id == null ? '-' : BOWLER_TYPE[id] || `BowlerType ${id}`;

export const categoryName = (id: number | null | undefined): string =>
  id == null ? '-' : BOWLING_CATEGORY[id] || `Category ${id}`;

export const shotTimingZoneName = (id: number | null | undefined): string =>
  id == null ? '-' : SHOT_TIMING_ZONE[id] || `Zone ${id}`;

export const shotTimingDirectionName = (id: number | null | undefined): string =>
  id == null ? '-' : SHOT_TIMING_DIRECTION[id] || `Dir ${id}`;
